<template>
  <DataTable
    v-model:selection="attachments_selected"
    :value="attachments"
    dataKey="id"
    tableStyle="min-width: 100%"
    stripedRows
    size="small"
    selectionMode="multiple"
    :isDataSelectable="(data) => can_delete(data.data)"
    data-test="documents-upload-table"
    :pt="{
      bodyRow: () => ({ 'data-test': 'documents-upload-table-row' }),
    }"
  >
    <template
      v-if="attachments_selected.length > 0"
      #header
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-lg font-semibold">
          {{ $t('document.labels.selected', { count: attachments_selected.length }) }}
        </span>
        <div class="flex items-center gap-2">
          <div
            v-if="show_category"
            class="flex items-center gap-1"
          >
            <span class="text-sm">{{ $t('document.labels.changeCategory') }}</span>
            <SelectDocumentCategory
              :attachment_category_id="null"
              @selectCategory="updateCategoryBulk"
            />
          </div>
          <ButtonDestructive
            v-if="can_delete_selected"
            class="!border-white !text-white hover:!border-[#ef4444] hover:!bg-transparent hover:!text-[#ef4444]"
            data-test="documents-upload-bulk-delete"
            @click="confirmDelete()"
          />
        </div>
      </div>
    </template>

    <Column
      selectionMode="multiple"
      headerStyle="width: 3rem"
      bodyClass="align-middle"
    />

    <Column bodyClass="align-middle">
      <template #body="{ data }">
        <div
          v-if="
            !isFileTooLarge(data) &&
            data.attributes.file_mime_type.startsWith('image') &&
            !data.attributes.file_mime_type.endsWith('heic')
          "
        >
          <img
            :src="data.attributes.file_url_s"
            :alt="data.attributes.file_name"
            class="h-12.5 w-12.5 rounded-md"
          />
        </div>
        <div v-else>
          <div
            :class="getPlaceholder(data).bgColor"
            class="flex size-12.5 items-center justify-center rounded-md"
          >
            <FontAwesomeIcon
              :class="getPlaceholder(data).color"
              :icon="getPlaceholder(data).icon"
              class="text-2xl"
            />
          </div>
        </div>
      </template>
    </Column>

    <Column
      :header="$t('document.attributes.name')"
      bodyClass="align-middle"
      style="max-width: 300px"
    >
      <template #body="{ data }">
        <div>
          <div class="truncate">
            {{ data.attributes.name }}
          </div>
          <div class="text-sm text-gray-500 italic">
            {{ data.attributes.file_ext.toUpperCase() }}
            &bull;
            {{ formatFileSize(data.attributes.file_size) }}
          </div>

          <div
            v-if="proposal_signature_required && !drawerIsFullPage"
            class="pt-2"
            @click.stop
          >
            <div
              v-if="!isSignaturePossible(data)"
              class="text-sm font-bold text-gray-500"
            >
              {{
                isFileTooLarge(data)
                  ? $t('signature.labels.signatureNotPossibleSize')
                  : $t('signature.labels.signatureNotPossible')
              }}
            </div>
            <ButtonSecondary
              v-else-if="!getDocumentSigned(data)"
              data-test="specify-region-btn"
              @onClick="openSignatureModal(data)"
            >
              {{ $t('signature.labels.specifyWhereToSign') }}
            </ButtonSecondary>
            <span
              v-else
              class="has-text-success flex cursor-pointer items-center gap-1"
              data-test="region-marked-label"
              @click="openSignatureModal(data)"
            >
              <FontAwesomeIcon icon="check" />
              <span class="text-sm font-bold">
                {{ $t('signature.labels.marked') }}
              </span>
              <FontAwesomeIcon
                class="cursor-pointer"
                data-test="region-edit"
                :icon="['far', 'pen']"
              />
            </span>
          </div>
        </div>
      </template>
    </Column>

    <Column
      v-if="proposal_signature_required && drawerIsFullPage"
      :header="$t('document.labels.signature')"
      bodyClass="align-middle"
    >
      <template #body="{ data }">
        <div @click.stop>
          <div
            v-if="!isSignaturePossible(data)"
            class="text-sm font-bold text-gray-500"
          >
            {{ $t('signature.labels.signatureNotPossible') }}
          </div>
          <ButtonSecondary
            v-else-if="!getDocumentSigned(data)"
            data-test="specify-region-btn2"
            @onClick="openSignatureModal(data)"
          >
            {{ $t('signature.labels.specifyWhereToSign') }}
          </ButtonSecondary>
          <span
            v-else
            class="has-text-success flex cursor-pointer items-center gap-1"
            data-test="region-marked-label"
            @click="openSignatureModal(data)"
          >
            <FontAwesomeIcon icon="check" />
            <span class="text-sm font-bold">
              {{ $t('signature.labels.marked') }}
            </span>
            <FontAwesomeIcon
              class="cursor-pointer"
              data-test="region-edit"
              :icon="['far', 'pen']"
            />
          </span>
        </div>
      </template>
    </Column>

    <Column
      v-if="show_category"
      :header="$t('document.attributes.category')"
      bodyClass="align-middle"
    >
      <template #body="{ data }">
        <SelectDocumentCategory
          :attachment_category_id="data.attributes.attachment_category_id"
          @selectCategory="(category_id) => updateCategory(data, category_id)"
          @click.stop
        />
      </template>
    </Column>

    <Column
      :header="$t('document.labels.actions')"
      bodyClass="align-middle"
    >
      <template #body="{ data }">
        <div
          class="flex gap-2"
          @click.stop
        >
          <FontAwesomeIcon
            class="cursor-pointer"
            :icon="['far', 'pen']"
            data-test="edit-document"
            @click="openEditModal(data)"
          />
          <FontAwesomeIcon
            v-if="can_delete(data)"
            class="cursor-pointer"
            icon="trash-can"
            data-test="delete-document"
            @click="confirmDelete(data)"
          />
        </div>
      </template>
    </Column>
  </DataTable>

  <ThePrimeModal
    v-if="modal_edit_visible"
    :is_small="false"
    :z_index="1300"
    @actionCancel="closeEditModal"
    @actionPrimary="saveEdit"
  >
    <template #title>
      {{ $t('document.labels.editDocumentName') }}
    </template>
    <template #subtitle>
      {{ $t('document.labels.editDocumentNameSubtitle') }}
    </template>
    <template #content>
      <div class="w-full pt-4">
        <InputText
          id="document_name"
          ref="edit_name_modal"
          v-model.trim="name_edit"
          class="p-inputtext w-full"
          type="text"
        />
      </div>
    </template>
    <template #ButtonPrimary>
      {{ $t('button.actions.save') }}
    </template>
  </ThePrimeModal>

  <SignatureIndicatorModal
    v-if="!is_template && signature_modal_visible && signature_modal_document_prop"
    :document_prop="signature_modal_document_prop"
    :signatures_prop="signature_modal_signatures"
    :project="project"
    @close="closeSignatureModal"
    @attachmentSignedCreated="
      (id) => emits('attachmentSignedCreated', { id, document_base: signature_modal_document_base })
    "
  />

  <SignatureTemplateIndicatorModal
    v-if="is_template && signature_modal_visible && signature_modal_document_prop"
    :document_template_prop="signature_modal_document_prop"
    :signature_templates_prop="signature_modal_signatures"
    :project="project"
    @close="closeSignatureModal"
    @attachmentTemplateSignedCreated="
      (id) => emits('attachmentSignedCreated', { id, document_base: signature_modal_document_base })
    "
  />
</template>

<script setup>
import { computed, inject, nextTick, ref } from 'vue';
import { formatFileSize } from '@/app/util/formatting_functions';
import { PROFILE_TYPES, RECORD_TYPE } from '@/app/data/model_constants';
import { MAX_FILE_SIZE_PREVIEW } from '@/app/data/document_constants';
import SelectDocumentCategory from '@/app/shared_components/selects/SelectDocumentCategory.vue';
import ThePrimeModal from '@/app/shared_components/modals/ThePrimeModal.vue';
import ButtonDestructive from '@/app/shared_components/buttons/ButtonDestructive.vue';
import SignatureIndicatorModal from '@/app/shared_components/documents/SignatureIndicatorModal.vue';
import SignatureTemplateIndicatorModal from '@/app/shared_components/documents/SignatureTemplateIndicatorModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import useGlobalConfirm from '@/composables/useGlobalConfirm.js';
import { useI18n } from 'vue-i18n';

const { current_profile } = mapGetters(['current_profile']);
const { UPDATE_ATTACHMENT, UPDATE_ATTACHMENT_TEMPLATE } = mapActions([
  'UPDATE_ATTACHMENT',
  'UPDATE_ATTACHMENT_TEMPLATE',
]);

const props = defineProps({
  attachments: { type: Array, required: true },
  show_category: { type: Boolean, default: false },
  is_template: { type: Boolean, default: false },
  proposal: { type: Object, default: null },
  project: { type: Object, default: null },
  documents_signed: { type: Array, default: () => [] },
  signatures: { type: Object, default: null },
});

const drawerIsFullPage = inject('drawerIsFullPage', ref(false));

const emits = defineEmits(['removeDocument', 'attachmentSignedCreated']);

const { confirm } = useGlobalConfirm();
const { t } = useI18n();
const attachments_selected = ref([]);
const modal_edit_visible = ref(false);
const documents_to_delete = ref([]);
const document_edit = ref(null);
const name_edit = ref('');
const edit_name_modal = ref(null);

const signature_modal_visible = ref(false);
const signature_modal_document_base = ref(null);

const can_delete_selected = computed(() => attachments_selected.value.some((doc) => can_delete(doc)));
const proposal_signature_required = computed(() => !!_.get(props.proposal, 'attributes.signature_required', false));

const record_types = computed(() => ({
  attachment: props.is_template ? RECORD_TYPE.ATTACHMENT_TEMPLATE : RECORD_TYPE.ATTACHMENT,
  document: props.is_template ? RECORD_TYPE.DOCUMENT_TEMPLATE : RECORD_TYPE.DOCUMENT,
  proposal: props.is_template ? RECORD_TYPE.PROPOSAL_TEMPLATE : RECORD_TYPE.PROPOSAL,
}));

const attachment_id_field = computed(() => (props.is_template ? 'attachment_template_id' : 'attachment_id'));

const getId = (document) => {
  const type_to_id_map = {
    [record_types.value.attachment]: document.id,
    [record_types.value.document]: document.attributes[attachment_id_field.value],
  };
  return type_to_id_map[document.type];
};

const getDocumentSigned = (document_base) =>
  props.documents_signed.find((ds) => ds.attributes[`base_${attachment_id_field.value}`] === getId(document_base)) ||
  null;

const signature_modal_document_prop = computed(() => {
  if (!signature_modal_document_base.value) return null;
  const doc_signed = getDocumentSigned(signature_modal_document_base.value);
  // Prefer signed document (has persisted file_url). Fall back to base only if it has a file_url.
  if (doc_signed) return doc_signed;
  return signature_modal_document_base.value;
});

const signature_modal_signatures = computed(() => {
  const doc_signed = signature_modal_document_base.value
    ? getDocumentSigned(signature_modal_document_base.value)
    : null;
  if (!doc_signed) return [];
  return _.filter(props.signatures, [`attributes.${attachment_id_field.value}`, getId(doc_signed)]);
});

const isSignaturePossible = (document_base) => {
  if (isFileTooLarge(document_base)) return false;
  const mime = document_base.attributes.file_mime_type.toLowerCase() || '';
  return ['image/jpg', 'image/jpeg', 'image/png'].includes(mime) || mime.startsWith('application/pdf');
};

const can_delete = (document_base) => {
  if (current_profile.value.type === PROFILE_TYPES.EMPLOYEE) return true;
  if (current_profile.value.type === PROFILE_TYPES.PARTNER) {
    // if type is 'Attachment' that means the file was just uploaded and has no representations (Document records) yet
    if (document_base.type === record_types.value.attachment) return true;
    const {
      attributes: { attachable_type, uploader_type },
    } = document_base;
    return attachable_type === record_types.value.proposal && uploader_type === PROFILE_TYPES.PARTNER;
  }
  return current_profile.value.type === PROFILE_TYPES.CUSTOMER;
};

const isFileTooLarge = (document) =>
  !document.attributes.file_versions && document.attributes.file_size > MAX_FILE_SIZE_PREVIEW;

const getPlaceholder = (document) => {
  const { file_ext, file_mime_type } = document.attributes;
  if (file_mime_type.startsWith('application/pdf'))
    return { icon: ['fas', 'file-pdf'], color: 'text-red-500', bgColor: 'bg-red-100' };
  if (['numbers', 'xlsx', 'xls'].includes(file_ext))
    return { icon: ['fas', 'file-excel'], color: 'text-green-500', bgColor: 'bg-green-100' };
  if (['pages', 'docx', 'doc'].includes(file_ext))
    return { icon: ['fas', 'file-word'], color: 'text-blue-500', bgColor: 'bg-blue-100' };
  if (['key', 'pptx', 'pps', 'ppt'].includes(file_ext))
    return { icon: ['fas', 'file-powerpoint'], color: 'text-orange-500', bgColor: 'bg-orange-100' };
  if (['heic', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'ico', 'webp'].includes(file_ext))
    return { icon: ['fas', 'file-image'], color: 'text-red-500', bgColor: 'bg-red-100' };
  if (
    [
      'mp3',
      'wav',
      'ogg',
      'm4a',
      'aac',
      'flac',
      'wma',
      'aiff',
      'au',
      'm4b',
      'm4p',
      'm4r',
      'm4v',
      'm4b',
      'm4p',
      'm4r',
      'm4v',
    ].includes(file_ext)
  ) {
    return { icon: ['fas', 'file-audio'], color: 'text-orange-500', bgColor: 'bg-orange-100' };
  }
  if (file_mime_type.startsWith('video'))
    return { icon: ['fas', 'file-video'], color: 'text-red-500', bgColor: 'bg-red-100' };
  return { icon: ['fas', 'file-lines'], color: 'text-gray-500', bgColor: 'bg-gray-100' };
};

const openEditModal = (document) => {
  document_edit.value = document;
  name_edit.value = document.attributes.name;
  modal_edit_visible.value = true;

  nextTick(() => {
    if (edit_name_modal.value.$el) {
      edit_name_modal.value.$el.select();
    }
  });
};

const closeEditModal = () => {
  modal_edit_visible.value = false;
  document_edit.value = null;
  name_edit.value = '';
};

const saveEdit = async () => {
  if (!name_edit.value.trim() || !document_edit.value) return;

  document_edit.value.attributes.name = name_edit.value;
  const updateAction = props.is_template ? UPDATE_ATTACHMENT_TEMPLATE : UPDATE_ATTACHMENT;
  await updateAction({ record: document_edit.value });

  const doc_signed = getDocumentSigned(document_edit.value);
  if (doc_signed) {
    doc_signed.attributes.name = name_edit.value;
    await updateAction({ record: doc_signed });
  }

  closeEditModal();
};

const updateCategory = async (document, attachment_category_id) => {
  document.attributes.attachment_category_id = attachment_category_id;

  const doc_signed = getDocumentSigned(document);
  if (doc_signed) doc_signed.attributes.attachment_category_id = attachment_category_id;

  if (attachment_category_id) {
    const updateAction = props.is_template ? UPDATE_ATTACHMENT_TEMPLATE : UPDATE_ATTACHMENT;
    await updateAction({ record: document });
    if (doc_signed) {
      await updateAction({ record: doc_signed });
    }
  }
};

const updateCategoryBulk = async (attachment_category_id) => {
  if (!attachment_category_id) return;

  await Promise.all(attachments_selected.value.map((document) => updateCategory(document, attachment_category_id)));

  attachments_selected.value = [];
};

const confirmDelete = async (document = null) => {
  const candidates = document ? [document] : attachments_selected.value;
  documents_to_delete.value = candidates.filter(can_delete);
  if (documents_to_delete.value.length === 0) return;
  const confirmed = await confirm({
    destructive: true,
    header: t('document.actions.delete_documents_upload', { count: documents_to_delete.value.length }),
    message: t('document.labels.delete_documents', { count: documents_to_delete.value.length }),
    base_z_index: 1300,
  });
  if (confirmed) executeDelete();
};

const executeDelete = () => {
  emits('removeDocument', documents_to_delete.value);
  attachments_selected.value = [];
  documents_to_delete.value = [];
};
const openSignatureModal = (document_base) => {
  signature_modal_document_base.value = document_base;
  signature_modal_visible.value = true;
};

const closeSignatureModal = () => {
  signature_modal_visible.value = false;
  signature_modal_document_base.value = null;
};
</script>

<style scoped>
:deep(tr[data-p-selected='true']) {
  background-color: var(--color-primary-200) !important;
}

:deep(.p-datatable-header) {
  background-color: var(--color-primary-500);
  color: white;
  padding: 0.5rem 1rem !important;
  border-radius: 0.5rem 0.5rem 0 0;
}

:deep(.p-datatable-thead > tr > th) {
  padding: 1rem 0.5rem !important;
}

:deep(.p-datatable-table-container:first-child .p-datatable-thead > tr > th:first-child) {
  border-radius: 0.5rem 0 0 0;
}

:deep(.p-datatable-table-container:first-child .p-datatable-thead > tr > th:last-child) {
  border-radius: 0 0.5rem 0 0;
}

/* Round the last row (footer area) */
:deep(.p-datatable-tbody > tr:last-child > td:first-child) {
  border-radius: 0 0 0 0.5rem;
}

:deep(.p-datatable-tbody > tr:last-child > td:last-child) {
  border-radius: 0 0 0.5rem 0;
}

/* Make checkboxes square instead of circles */
:deep(.p-checkbox .p-checkbox-box) {
  border-radius: 0.25rem !important;
}
</style>
